// Navigation and layout management for Docospot
// Handles sidebar, header, page titles, and navigation permissions

import { t, getCurrentLanguage } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { getCurrentUserFromAPI } from '../core/api.js';
import { getCurrentUser } from '../utils/helpers.js';

/**
 * Update navigation visibility based on authentication status
 */
export function updateNavigation() {
    const userProfileSection = document.getElementById('user-profile-section');
    const sidebarMyItems = document.getElementById('sidebar-my-items');
    const sidebarLogout = document.getElementById('sidebar-logout');
    const languageSelector = document.getElementById('language-selector-container');
    const sidebar = document.getElementById('sidebar');
    const header = document.querySelector('header');
    const mainContent = document.getElementById('main-content');

    console.log('updateNavigation called');
    console.log('auth.token:', auth.token);
    console.log('auth.isAuthenticated():', auth.isAuthenticated());

    if (auth.isAuthenticated()) {
        // Show sidebar and header
        if (sidebar) sidebar.style.display = 'block';
        if (header) header.style.display = 'block';
        
        // Restore main content margins
        if (mainContent) {
            mainContent.style.marginLeft = '';
            mainContent.style.paddingTop = '';
        }
        
        if (sidebarMyItems) sidebarMyItems.style.display = 'block';
        if (sidebarLogout) sidebarLogout.style.display = 'block';
        
        // Show language selector
        if (languageSelector) {
            languageSelector.style.display = 'block';
            const select = document.getElementById('language-selector');
            if (select) {
                select.value = getCurrentLanguage();
            }
        }
        
        // Show user profile (text only, no image)
        if (userProfileSection) {
            userProfileSection.innerHTML = `
                <div class="flex items-center space-x-3">
                    <span class="text-sm font-medium text-gray-900" id="current-user-name">User</span>
                </div>
            `;
            userProfileSection.style.display = 'block';
        }
        
        // Load user info first, then show default accessible page
        loadCurrentUser().then(() => {
            showDefaultPage();
        }).catch((error) => {
            console.error('Failed to load user:', error);
            showDefaultPage(); // Show default page anyway
        });
    } else {
        // Hide sidebar and header for login screen
        if (sidebar) sidebar.style.display = 'none';
        if (header) header.style.display = 'none';
        
        // Make main content full width
        if (mainContent) {
            mainContent.style.marginLeft = '0';
            mainContent.style.paddingTop = '0';
        }
        
        if (sidebarMyItems) sidebarMyItems.style.display = 'none';
        if (sidebarLogout) sidebarLogout.style.display = 'none';
        if (userProfileSection) userProfileSection.style.display = 'none';
        if (languageSelector) languageSelector.style.display = 'none';
        
        // Hide setup menu when not authenticated
        updateSetupMenuVisibility(null);
        
        // Show login screen in main content
        showLoginScreen();
    }
}

/**
 * Load current user data from API
 */
export async function loadCurrentUser() {
    try {
        console.log('loadCurrentUser: Fetching user data from API');
        const user = await getCurrentUserFromAPI();
        console.log('loadCurrentUser: Got user data:', user);
        console.log('loadCurrentUser: account_name:', user.account_name);
        
        // Store user data in localStorage
        localStorage.setItem('current_user', JSON.stringify(user));
        
        const userNameElement = document.getElementById('current-user-name');
        if (userNameElement) {
            userNameElement.textContent = user.full_name || user.username;
        }
        
        // Initialize permissions and update navigation
        if (window.permissions) {
            window.permissions.init(user);
            updateNavigationPermissions();
        }
        
        // Initialize account switcher for SuperAdmin
        await initializeAccountSwitcher();
        
    } catch (error) {
        console.error('Failed to load user info:', error);
        // Hide navigation items on error
        updateSetupMenuVisibility(null);
        throw error; // Re-throw so caller knows it failed
    }
}

/**
 * Show login screen (redirects to /login page)
 */
export function showLoginScreen() {
    // Prevent redirect loop - don't redirect if already on login page
    if (window.location.pathname === '/login') {
        console.log('Already on login page, not redirecting');
        return;
    }
    console.log('Redirecting to login page');
    // Redirect to separate login page
    window.location.href = '/login';
}

/**
 * Update active navigation item styling
 * @param {string} activeItem - Navigation item ID
 */
export function updateActiveNavigation(activeItem) {
    // Remove any lingering floating bulk-action bars when switching pages
    document.getElementById('sensors-mass-action-bar')?.remove();
    document.getElementById('location-mass-action-bar')?.remove();

    // Remove active class from all navigation items
    document.querySelectorAll('nav a, nav button').forEach(link => {
        link.classList.remove('bg-primary-700', 'text-white');
        link.classList.add('text-gray-400', 'hover:bg-gray-700', 'hover:text-white');
    });
    
    // Add active class to current item
    const activeSelectors = {
        'dashboard': 'a[onclick="showDashboard()"]',
        'time-tracking': 'a[onclick="showTimeTracking()"]',
        'my-locations': 'a[onclick="showMyLocations()"]',
        'my-assets': 'a[onclick="showMyAssets()"]',
        'sensor-logs': 'a[onclick="showSensorLogs()"]',
        'alarms': 'a[onclick="showAlarms()"]',
        'reports': 'a[onclick="showReports()"]',
        'tasks': 'a[onclick="showTasks()"]',
        'users': 'a[onclick="showUsers()"]',
        'accounts': 'a[onclick="showAccounts()"]',
        'locations': 'a[onclick="showLocations()"]',
        'assets': 'a[onclick="showAssets()"]',
        'groups': 'a[onclick="showGroups()"]',
        'setup-alarms': 'a[onclick="showSetupAlarms()"]',
        'setup-sensors': 'a[onclick="showSetupSensors()"]',
        'settings': 'a[onclick="showSettings()"]'
    };
    
    if (activeSelectors[activeItem]) {
        document.querySelectorAll(activeSelectors[activeItem]).forEach(link => {
            link.classList.add('bg-primary-700', 'text-white');
            link.classList.remove('text-gray-400', 'hover:bg-gray-700', 'hover:text-white');
        });
    }

    // Clear location mass-selection state when navigating away from Locations
    if (activeItem !== 'locations' && typeof window.clearLocationSelection === 'function') {
        try { window.clearLocationSelection(); } catch (_) { /* no-op */ }
    }
    
    // Special handling for setup submenu items - also highlight the setup button
    const setupSubmenuItems = ['accounts', 'users', 'locations', 'assets', 'groups', 'setup-alarms', 'setup-sensors', 'settings'];
    if (setupSubmenuItems.includes(activeItem)) {
        const setupButton = document.querySelector('button[onclick="toggleSetupMenu()"]');
        if (setupButton) {
            setupButton.classList.add('bg-primary-700', 'text-white');
            setupButton.classList.remove('text-gray-400', 'hover:bg-gray-700', 'hover:text-white');
        }
        
        // Ensure submenu is open
        const submenu = document.getElementById('setup-submenu');
        const chevron = document.getElementById('setup-chevron');
        if (submenu && chevron) {
            submenu.classList.remove('hidden');
            chevron.style.transform = 'rotate(90deg)';
        }
    }
}

/**
 * Update page title in header
 * @param {string} title - Page title
 */
export function updatePageTitle(title) {
    const pageTitleElement = document.getElementById('page-title');
    if (pageTitleElement) {
        pageTitleElement.textContent = title;
    }
}

/**
 * Toggle setup menu visibility
 */
export function toggleSetupMenu() {
    const setupSubmenu = document.getElementById('setup-submenu');
    const setupChevron = document.getElementById('setup-chevron');
    
    if (!setupSubmenu || !setupChevron) return;
    
    const isHidden = setupSubmenu.classList.contains('hidden');
    
    if (isHidden) {
        // Show submenu
        setupSubmenu.classList.remove('hidden');
        setupChevron.style.transform = 'rotate(90deg)';
    } else {
        // Hide submenu
        setupSubmenu.classList.add('hidden');
        setupChevron.style.transform = 'rotate(0deg)';
    }
}

/**
 * Update navigation visibility based on user permissions
 */
export function updateNavigationPermissions() {
    if (!window.permissions || !window.permissions.checker) {
        console.warn('Permissions not initialized yet');
        return;
    }
    
    // Get permission checker
    const perms = window.permissions;
    
    // Get main menu items
    const dashboardLink = document.querySelector('a[onclick="showDashboard()"]');
    const timeTrackingLink = document.querySelector('a[onclick="showTimeTracking()"]');
    const setupButton = document.querySelector('button[onclick="toggleSetupMenu()"]');
    const setupSubmenu = document.getElementById('setup-submenu');
    
    // Dashboard - only Administrator, Team leader, SuperAdmin
    if (dashboardLink) {
        if (perms.canAccessMenu('dashboard')) {
            dashboardLink.closest('li').style.display = '';
        } else {
            dashboardLink.closest('li').style.display = 'none';
        }
    }
    
    // Setup menu - only Administrator, Team leader, SuperAdmin
    if (setupButton) {
        if (perms.canAccessMenu('setup')) {
            setupButton.closest('li').style.display = '';
        } else {
            setupButton.closest('li').style.display = 'none';
            // Also hide submenu if it's open
            if (setupSubmenu && !setupSubmenu.classList.contains('hidden')) {
                setupSubmenu.classList.add('hidden');
                const setupChevron = document.getElementById('setup-chevron');
                if (setupChevron) {
                    setupChevron.style.transform = 'rotate(0deg)';
                }
            }
        }
    }
    
    // Data menu items
    const myLocationsLink = document.querySelector('a[onclick="showMyLocations()"]');
    const myAssetsLink = document.querySelector('a[onclick="showMyAssets()"]');
    const sensorLogsLink = document.querySelector('a[onclick="showSensorLogs()"]');
    const alarmsLink = document.querySelector('a[onclick="showAlarms()"]');
    const reportsLink = document.querySelector('a[onclick="showReports()"]');
    const tasksLink = document.querySelector('a[onclick="showTasks()"]');

    if (timeTrackingLink) {
        timeTrackingLink.closest('li').style.display = perms.canAccessDataMenu('timeTracking') ? '' : 'none';
    }
    if (myLocationsLink) {
        myLocationsLink.closest('li').style.display = perms.canAccessDataMenu('myLocations') ? '' : 'none';
    }
    if (myAssetsLink) {
        myAssetsLink.closest('li').style.display = perms.canAccessDataMenu('myAssets') ? '' : 'none';
    }
    if (sensorLogsLink) {
        sensorLogsLink.closest('li').style.display = perms.canAccessDataMenu('sensorLogs') ? '' : 'none';
    }
    if (alarmsLink) {
        alarmsLink.closest('li').style.display = perms.canAccessDataMenu('alarms') ? '' : 'none';
    }
    if (reportsLink) {
        reportsLink.closest('li').style.display = perms.canAccessDataMenu('reports') ? '' : 'none';
    }
    if (tasksLink) {
        tasksLink.closest('li').style.display = perms.canAccessDataMenu('tasks') ? '' : 'none';
    }

    console.log('✅ Navigation permissions updated for role:', perms.checker.userRole);
}

/**
 * Legacy function for backwards compatibility - now uses permissions
 * @param {string} userRole - User role
 */
export function updateSetupMenuVisibility(userRole) {
    // Initialize permissions if needed
    if (userRole && window.permissions) {
        if (!window.permissions.checker || window.permissions.checker.userRole !== userRole) {
            window.permissions.init({ role: userRole });
        }
    }
    
    // Use the new permission-based function
    updateNavigationPermissions();
}

/**
 * Show default accessible page based on user permissions
 */
export function showDefaultPage() {
    // Route to the first accessible page based on user permissions
    if (!window.permissions || !window.permissions.checker) {
        console.warn('Permissions not initialized, defaulting to login');
        showLoginScreen();
        return;
    }
    
    const perms = window.permissions;
    
    // Try Dashboard first (Administrator, Team leader, SuperAdmin)
    if (perms.canAccessMenu('dashboard') && window.showDashboard) {
        window.showDashboard();
        return;
    }
    
    // Try Data menu (all roles should have access to at least one data menu)
    if (perms.canAccessDataMenu('timeTracking') && window.showTimeTracking) {
        window.showTimeTracking();
        return;
    }
    
    if (perms.canAccessDataMenu('sensorLogs') && window.showSensorLogs) {
        window.showSensorLogs();
        return;
    }
    
    if (perms.canAccessDataMenu('accounts') && window.showAccounts) {
        window.showAccounts();
        return;
    }
    
    if (perms.canAccessDataMenu('users') && window.showUsers) {
        window.showUsers();
        return;
    }
    
    if (perms.canAccessDataMenu('locations') && window.showLocations) {
        window.showLocations();
        return;
    }
    
    if (perms.canAccessDataMenu('sensors') && window.showSensors) {
        window.showSensors();
        return;
    }
    
    // Fallback - shouldn't happen if permissions are configured correctly
    console.warn('No accessible pages found for role:', perms.checker.userRole);
    if (window.showError) {
        window.showError('You do not have access to any pages. Please contact your administrator.');
    }
}

/**
 * Initialize account switcher for SuperAdmin
 * Fetches accounts and populates the dropdown
 */
export async function initializeAccountSwitcher() {
    const user = getCurrentUser();
    const switcherContainer = document.getElementById('account-switcher-container');
    const switcher = document.getElementById('account-switcher');
    
    // Only show for SuperAdmin
    if (!user || user.role !== 'SuperAdmin') {
        if (switcherContainer) switcherContainer.style.display = 'none';
        return;
    }
    
    try {
        // Fetch all accounts
        const response = await fetch('/api/accounts/', {
            headers: {
                'Authorization': `Bearer ${auth.token}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) {
            throw new Error('Failed to fetch accounts');
        }
        
        const accounts = await response.json();
        
        // Populate dropdown
        switcher.innerHTML = '<option value="">-- Select Account --</option>';
        accounts.forEach(account => {
            const option = document.createElement('option');
            option.value = account._id;
            option.textContent = account.hierarchy_display || account.account_name;
            switcher.appendChild(option);
        });
        
        // Set current selection from localStorage
        const selectedAccount = localStorage.getItem('superadmin_selected_account');
        if (selectedAccount) {
            switcher.value = selectedAccount;
        }
        
        // Add change handler
        switcher.onchange = async function() {
            const accountId = this.value;
            const { setSuperAdminAccount } = await import('../utils/helpers.js');
            await setSuperAdminAccount(accountId);
        };
        
        // Show the switcher
        if (switcherContainer) switcherContainer.style.display = 'block';
        
        // Show warning if no account selected
        if (!selectedAccount) {
            if (window.showError) {
                window.showError('Please select an account to view data');
            }
        }
        
    } catch (error) {
        console.error('Failed to initialize account switcher:', error);
    }
}

export default {
    updateNavigation,
    loadCurrentUser,
    showLoginScreen,
    updateActiveNavigation,
    updatePageTitle,
    toggleSetupMenu,
    updateNavigationPermissions,
    updateSetupMenuVisibility,
    showDefaultPage,
    initializeAccountSwitcher
};
